import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
const Card = ({ children }) => (
    <div className="bg-white shadow-md rounded-xl p-4">{children}</div>
  );
  
  const CardContent = ({ children }) => (
    <div className="p-4">{children}</div>
  );

const ResultCard = ({ title, value, subtext }) => (
  <Card className="bg-white shadow-md rounded-xl pt-4">
    <CardContent>
      <motion.h3
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="text-lg font-semibold text-pink-600 mb-2 text-center"
      >
        {title}
      </motion.h3>
      {subtext && <p className="text-center text-gray-700">{subtext}</p>}
      <p className="text-center text-gray-700">{value}</p>
    </CardContent>
  </Card>
);

const calculateOvulationDates = (startDate, cycleLength) => {
  const parsedDate = dayjs(startDate);
  const cycle = cycleLength === "Más de 35 días" ? 36 : parseInt(cycleLength, 10);

  const nextPeriod = parsedDate.add(cycle, 'day');
  const ovulationDay = nextPeriod.subtract(14, 'day');

  return {
    nextPeriod: nextPeriod.format('DD/MM/YYYY'),
    ovulationDay: ovulationDay.format('DD/MM/YYYY'),
    dueDate: parsedDate.add(280, 'day').format('DD/MM/YYYY'),
    fertileStart: ovulationDay.subtract(5, 'day').format('DD/MM/YYYY'),
    fertileEnd: ovulationDay.add(1, 'day').format('DD/MM/YYYY')
  };
};

export default function OvulationCalculatorApp() {
  const [lastPeriodDate, setLastPeriodDate] = useState("");
  const [cycleLength, setCycleLength] = useState("28");
  const [results, setResults] = useState({
    nextPeriod: "",
    ovulationDay: "",
    dueDate: "",
    fertileStart: "",
    fertileEnd: ""
  });
  const [error, setError] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // FAQ state
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const faqs = [
    {
      question: "¿Cómo sé cuál es la duración de mi ciclo?",
      answer: "La duración del ciclo se cuenta desde el primer día de menstruación hasta el día antes de que comience la siguiente."
    },
    {
      question: "¿Es 100% precisa esta calculadora?",
      answer: "La calculadora es una estimación. Factores como el estrés o condiciones de salud pueden alterar tu ciclo."
    },
    {
      question: "¿Qué hago si mis ciclos son irregulares?",
      answer: "Si tus ciclos varían mucho, lo ideal es que consultes con un especialista para un diagnóstico más preciso."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index));
  };

  const handleCalculate = useCallback(async () => {
    setIsCalculating(true);
    setError(null);

    const parsedDate = dayjs(lastPeriodDate);
    if (!parsedDate.isValid()) {
      setError("Por favor, ingresa una fecha válida");
      setIsCalculating(false);
      return;
    }
    if (!cycleLength || (isNaN(parseInt(cycleLength)) && cycleLength !== "Más de 35 días")) {
      setError("Por favor, selecciona una duración de ciclo válida");
      setIsCalculating(false);
      return;
    }

    const newResults = calculateOvulationDates(lastPeriodDate, cycleLength);
    setResults(newResults);
    setIsCalculating(false);
  }, [lastPeriodDate, cycleLength]);

  const handleCapture = useCallback(async () => {
    const element = document.getElementById('captureArea');
    if (!element) return;

    try {
      const canvas = await html2canvas(element);
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'calculadora_ovulacion.png';
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error al capturar la imagen:', error);
    }
  }, []);

  return (
    <>
      {/* Botón de WhatsApp flotante en esquina inferior derecha */}
      <a
        href="https://wa.link/ubbtn9" // Reemplaza con número real
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 bg-green-500 p-3 rounded-full shadow-lg hover:bg-green-600 transition-colors"
      >
        {/* SVG de WhatsApp en blanco */}
        <svg
          fill="#ffffff"
          version="1.1"
          id="Capa_1"
          xmlns="http://drangelafertilidad.com/wp-content/uploads/2025/05/Recurso-4angela-quispe-fertilidad.svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 30.667 30.667"
          xmlSpace="preserve"
          width="30"
          height="30"
        >
          <path d="M30.667,14.939c0,8.25-6.74,14.938-15.056,14.938c-2.639,0-5.118-0.675-7.276-1.857L0,30.667l2.717-8.017 c-1.37-2.25-2.159-4.892-2.159-7.712C0.559,6.688,7.297,0,15.613,0C23.928,0.002,30.667,6.689,30.667,14.939z M15.61,2.382 c-6.979,0-12.656,5.634-12.656,12.56c0,2.748,0.896,5.292,2.411,7.362l-1.58,4.663l4.862-1.545c2,1.312,4.393,2.076,6.963,2.076 c6.979,0,12.658-5.633,12.658-12.559C28.27,8.016,22.59,2.382,15.61,2.382z M23.214,18.38c-0.094-0.151-0.34-0.243-0.708-0.427 c-0.367-0.184-2.184-1.069-2.521-1.189c-0.34-0.123-0.586-0.185-0.832,0.182c-0.243,0.367-0.951,1.191-1.168,1.437 c-0.215,0.245-0.43,0.276-0.799,0.095c-0.369-0.186-1.559-0.57-2.969-1.817c-1.097-0.972-1.838-2.169-2.052-2.536 c-0.217-0.366-0.022-0.564,0.161-0.746c0.165-0.165,0.369-0.428,0.554-0.643c0.185-0.213,0.246-0.364,0.369-0.609 c0.121-0.245,0.06-0.458-0.031-0.643c-0.092-0.184-0.829-1.984-1.138-2.717c-0.307-0.732-0.614-0.611-0.83-0.611 c-0.215,0-0.461-0.03-0.707-0.03S9.897,8.215,9.56,8.582s-1.291,1.252-1.291,3.054c0,1.804,1.321,3.543,1.506,3.787 c0.186,0.243,2.554,4.062,6.305,5.528c3.753,1.465,3.753,0.976,4.429,0.914c0.678-0.062,2.184-0.885,2.49-1.739 C23.307,19.268,23.307,18.533,23.214,18.38z" />
        </svg>
      </a>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-4 flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-pink-50 to-pink-100"
      >
        <div id="captureArea" className="w-full max-w-lg">
          <Card className="w-full shadow-xl rounded-2xl p-4">
            <CardContent>
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-2xl font-bold text-pink-600 mb-1 text-center"
              >
                Calculadora de Ovulación
              </motion.h1>
              <p className="text-xs text-center text-pink-500 mb-4">por Dra. Angela Quispe</p>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-red-500 text-center mb-4"
                >
                  {error}
                </motion.div>
              )}

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="grid gap-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="last-period">
                    Fecha de tu último periodo:
                  </label>
                  <input
                    id="last-period"
                    type="date"
                    value={lastPeriodDate}
                    onChange={(e) => setLastPeriodDate(e.target.value)}
                    className="w-full p-2 border border-pink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-400"
                    aria-label="Fecha de tu último periodo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cycle-length">
                    Duración promedio de tu ciclo (días):
                  </label>
                  <select
                    id="cycle-length"
                    value={cycleLength}
                    onChange={(e) => setCycleLength(e.target.value)}
                    className="w-full p-2 border border-pink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-400"
                    aria-label="Duración promedio de tu ciclo"
                  >
                    <option value="24">24</option>
                    <option value="25">25</option>
                    <option value="26">26</option>
                    <option value="27">27</option>
                    <option value="28">28</option>
                    <option value="29">29</option>
                    <option value="30">30</option>
                    <option value="31">31</option>
                    <option value="32">32</option>
                    <option value="33">33</option>
                    <option value="34">34</option>
                    <option value="35">35</option>
                    <option value="Más de 35 días">Más de 35 días</option>
                  </select>
                </div>

                <button
                  onClick={handleCalculate}
                  disabled={isCalculating}
                  className="mt-2 bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-2xl transition-all"
                >
                  {isCalculating ? "Calculando..." : "Calcular"}
                </button>
              </motion.div>

              {results.nextPeriod && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="mt-6"
                >
                  <h2 className="text-xl font-semibold text-pink-600 mb-4">Resultados:</h2>
                  <div className="grid gap-4 grid-cols-1">
                    <ResultCard title="Próximo día de menstruación" value={results.nextPeriod} />
                    <ResultCard title="Próximo día de ovulación" value={results.ovulationDay} />
                    <ResultCard
                      title="Ventana fértil"
                      value={`${results.fertileStart} - ${results.fertileEnd}`}
                      subtext="Intentar concebir entre estos días"
                    />
                    <ResultCard title="Fecha de parto aproximada" value={results.dueDate} />
                  </div>
                  <button
                    onClick={() =>  window.open("https://wa.link/ubbtn9", "_blank")}
                    
                    className="mt-4 bg-pink-500 hover:bg-pink-600 text-white font-bold py-2 px-4 rounded-2xl w-full"
                  >
                    Programar consulta de fertilidad
                  </button>
                </motion.div>
              )}

              {results.nextPeriod && (
                <button
                  onClick={handleCapture}
                  className="mt-4 bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded-2xl w-full"
                >
                  Guardar Captura
                </button>
              )}
            </CardContent>
          </Card>

          {/* Sección de Preguntas Frecuentes antes del disclaimer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="mt-8 bg-white rounded-xl shadow-md p-4"
          >
            <h2 className="text-lg font-semibold text-pink-600 mb-4">Preguntas Frecuentes</h2>
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-pink-200 py-2">
                <button
                  className="flex justify-between w-full text-left"
                  onClick={() => toggleFaq(index)}
                >
                  <span className="font-bold text-gray-700 text-sm">{faq.question}</span>
                  <span className="text-pink-600 font-bold">{openFaqIndex === index ? '−' : '+'}</span>
                </button>
                <AnimatePresence>
                  {openFaqIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden mt-2"
                    >
                      <p className="text-sm text-gray-600">{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="mt-4 text-center text-gray-600"
          >
            <p className="text-sm">
              <strong>Descargo de responsabilidad:</strong> Esta calculadora sirve como referencia y no reemplaza la opinión médica profesional.
              Siempre consulta con un especialista para información personalizada.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
}